import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useStyles } from './useStyles';
import { Menu, MenuItem } from '../../material-ui/Menu';
import {
    cancelMultiedit,
    multieditFrom,
    multieditTo,
    removeEntry,
    revertDirection,
    showSpecialPointModal,
} from '../../../actions/navigation';
import { TYPES, useShowModal } from '../../../actions/modal';
import { ListItemIcon, ListItemText } from '../../material-ui/List';
import Comment from '../../../icons/image/Comment';
import Hold from '../../../icons/image/Hold';
import CheckAltitude from '../../../icons/image/CheckAltitude';
import Add from '../../../icons/image/Add';
import TwoArrowBifurcation from '../../../icons/image/TwoArrowBifurcation';
import Rubbish from '../../../icons/image/Rubbish';
import Edit from '../../../icons/image/Edit';
import { NavType } from '../constants';
import Direct from '../../../icons/image/Direct';
import RightRefresh from '../../../icons/image/RightRefresh';
import Download from '../../../icons/image/Download';
import Cancel from '../../../icons/image/Cancel';
import Divert from '../../../icons/image/Divert';
import { stopPropagation } from '../../service/eventHandlers';

const getModalType = (type, sign) => {
    return sign || type === NavType.TOC || type === NavType.TOD ? TYPES.SPECIAL_POINT : TYPES.POINT;
};

const validatePointCoordinates = ({ lat, long }) => {
    return !!(lat && long);
};

export const PointMenuTypes = {
    DEFAULT: 0,
    POINT: 1,
    DIRECTED: 2,
};

export const RouteItemMenu = memo((props) => {
    const { offset, point, pos, type = PointMenuTypes.DEFAULT, onClose, anchorEl, ...rest } = props;
    const classes = useStyles();
    const dispatch = useDispatch();
    const showModal = useShowModal();
    const { t: translate } = useTranslation();
    const handleRemoveEntry = React.useCallback(
        (event) => {
            stopPropagation(event);
            dispatch(removeEntry(pos));
            onClose();
        },
        [pos]
    );

    const handleCloseMenu = React.useCallback((event) => {
        stopPropagation(event);
        onClose();
    }, []);

    const handleShowSpecialPoint = React.useCallback(
        (event) => {
            stopPropagation(event);
            dispatch(
                showSpecialPointModal({
                    title: translate('navigationRoute.addSpecialPoint'),
                    pos,
                    point,
                    isUserAdd: true,
                    isNewPoint: true,
                })
            );
            onClose();
        },
        [point, pos]
    );

    const handleShowCheckAltitude = React.useCallback(
        (event) => {
            stopPropagation(event);
            showModal(TYPES.CHECK_ALTITUDE, {
                title: translate('navigationRoute.addCheckAltitude'),
                pos,
                point,
                isNewPoint: true,
            });
            onClose();
        },
        [point, pos]
    );

    const handleShowHold = React.useCallback(
        (event) => {
            stopPropagation(event);
            showModal(TYPES.HOLD, {
                title: translate('navigationRoute.addHold'),
                pos,
                point,
                isNewPoint: true,
            });
            onClose();
        },
        [pos, point]
    );

    const handleShowOffset = React.useCallback(
        (event) => {
            stopPropagation(event);
            showModal(TYPES.OFFSET, {
                title: translate('navigationRoute.setOffset').toUpperCase(),
                pos,
                point,
            });
            onClose();
        },
        [pos, point]
    );

    const handleShowComment = React.useCallback(
        (event) => {
            stopPropagation(event);
            showModal(TYPES.COMMENT, {
                pos,
                point,
                isNewPoint: true,
            });
            onClose();
        },
        [pos, point]
    );

    const handleEditClick = React.useCallback(
        (event) => {
            stopPropagation(event);
            const { route, rnt } = point || {};
            const title = `${translate('navigationRoute.editPointRoute', {
                route: route || rnt || '',
                rnt: rnt && route && rnt !== route ? `(${rnt})` : '',
            })} (${translate('navigationRoute.fullMode').toLowerCase()})`;

            showModal(getModalType(point.type, point.isUserAdd), {
                title,
                pos,
                point,
                isUserAdd: point.isUserAdd,
            });
            onClose();
        },
        [pos, point]
    );

    const handleDirect = React.useCallback(
        (event) => {
            stopPropagation(event);
            showModal(TYPES.DIRECT, {
                title: translate('navigationRoute.direct').toLowerCase(),
                pointPosFrom: pos,
            });
            onClose();
        },
        [pos]
    );

    const handleChangeDirection = React.useCallback(
        (event) => {
            stopPropagation(event);
            showModal(TYPES.DIRECT, {
                title: translate('navigationRoute.direct').toLowerCase(),
                pointPosFrom: pos,
                editMode: point.isRemoveFromDirecting,
            });
            onClose();
        },
        [pos, point]
    );

    const handleRevertDirection = React.useCallback(
        (event) => {
            stopPropagation(event);
            dispatch(revertDirection(pos));
            onClose();
        },
        [pos, point]
    );

    const handleToMultiedit = React.useCallback(
        (event) => {
            stopPropagation(event);
            dispatch(multieditTo(pos));
            showModal(TYPES.MULTIEDIT, {
                pos,
                point,
                title: translate('navigationRoute.multieditPoint'),
                multieditTo: pos,
            });
            onClose();
        },
        [pos, point]
    );

    const handleMultiedit = React.useCallback(
        (event) => {
            stopPropagation(event);
            dispatch(multieditFrom(pos));
            onClose();
        },
        [pos]
    );

    const handleCancelMultiedit = React.useCallback((event) => {
        stopPropagation(event);
        dispatch(cancelMultiedit());
        onClose();
    }, []);

    const handleShowAlternateRoutes = React.useCallback(
        (event) => {
            stopPropagation(event);
            showModal(TYPES.ALTERNATE_ROUTE, {
                title: translate('navigationRoute.alternateRoute'),
                pos,
            });
            onClose();
        },
        [pos]
    );

    return (
        <Menu id="simple-menu" anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleCloseMenu}>
            {type !== PointMenuTypes.DEFAULT && (
                <MenuItem onClick={handleEditClick}>
                    <ListItemIcon>
                        <Edit fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={translate('navigationRoute.editPoint')} />
                </MenuItem>
            )}
            <MenuItem onClick={handleShowComment}>
                <ListItemIcon>
                    <Comment fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={translate('navigationRoute.addComment')} />
            </MenuItem>
            {type !== PointMenuTypes.DIRECTED && (
                <MenuItem onClick={handleShowHold}>
                    <ListItemIcon>
                        <Hold fontSize="small" className={classes.icon} />
                    </ListItemIcon>
                    <ListItemText primary={translate('navigationRoute.addHold')} />
                </MenuItem>
            )}
            <MenuItem onClick={handleShowCheckAltitude}>
                <ListItemIcon>
                    <CheckAltitude fontSize="small" className={classes.icon} />
                </ListItemIcon>
                <ListItemText primary={translate('navigationRoute.checkAltitude')} />
            </MenuItem>
            {type !== PointMenuTypes.DIRECTED && (
                <MenuItem onClick={handleShowSpecialPoint}>
                    <ListItemIcon>
                        <Add fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={translate('navigationRoute.addWaypoint')} />
                </MenuItem>
            )}
            <MenuItem onClick={handleShowOffset}>
                <ListItemIcon>
                    <TwoArrowBifurcation fontSize="small" />
                </ListItemIcon>
                <ListItemText
                    primary={
                        <>
                            <span>{translate('navigationRoute.offset')}</span>
                            {offset && (
                                <span style={{ fontWeight: 'bold' }}>
                                    {offset > 0 ? 'R' : 'L'}
                                    {Math.abs(offset)}
                                </span>
                            )}
                        </>
                    }
                />
            </MenuItem>
            {type !== PointMenuTypes.DIRECTED && (
                <MenuItem onClick={handleRemoveEntry}>
                    <ListItemIcon>
                        <Rubbish fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={translate('navigationRoute.deletePoint')} />
                </MenuItem>
            )}
            {type === PointMenuTypes.POINT && (
                <MenuItem onClick={handleDirect} disabled={!validatePointCoordinates(point)}>
                    <ListItemIcon>
                        <Direct fontSize="small" className={classes.icon} />
                    </ListItemIcon>
                    <ListItemText primary={translate('navigationRoute.direct')} />
                </MenuItem>
            )}
            {type === PointMenuTypes.POINT && point.isDirectionFrom && (
                <MenuItem onClick={handleRevertDirection}>
                    <ListItemIcon>
                        <RightRefresh fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={translate('navigationRoute.cancelDirection')} />
                </MenuItem>
            )}
            {type === PointMenuTypes.POINT && rest.multieditFrom < pos && (
                <MenuItem onClick={handleToMultiedit}>
                    <ListItemIcon>
                        <Download fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={translate('navigationRoute.multieditTo')} />
                </MenuItem>
            )}
            {type === PointMenuTypes.POINT && rest.multieditFrom === undefined && (
                <MenuItem onClick={handleMultiedit}>
                    <ListItemIcon>
                        <Edit fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={translate('navigationRoute.multieditFrom')} />
                </MenuItem>
            )}
            {type === PointMenuTypes.POINT && rest.multieditFrom !== undefined && (
                <MenuItem onClick={handleCancelMultiedit}>
                    <ListItemIcon>
                        <Cancel fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={translate('navigationRoute.cancelMultiedit')} />
                </MenuItem>
            )}
            {type === PointMenuTypes.POINT && (
                <MenuItem onClick={handleShowAlternateRoutes}>
                    <ListItemIcon>
                        <Divert fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={translate('navigationRoute.divert')} />
                </MenuItem>
            )}
            {type === PointMenuTypes.DIRECTED && (
                <MenuItem onClick={handleChangeDirection}>
                    <ListItemIcon>
                        <Direct fontSize="small" className={classes.icon} />
                    </ListItemIcon>
                    <ListItemText primary={translate('navigationRoute.changeDirection')} />
                </MenuItem>
            )}
        </Menu>
    );
});
